// Receivables & collections (admins + managers). Three views over the payment schedule the platform
// already holds: AR aging (who owes what, by how far past due), a cash-flow forecast (when unpaid
// installments are expected to land), and per-client arrears — all combined in AUD (home currency).
import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import { fetchReceivables } from '../api/receivables.js';
import { fmtMoney } from '../lib/matrix.js';
import { storeShort } from '../lib/stores.js';

const AUD = (n) => fmtMoney(n || 0, 'AUD');

// Aging buckets, worst → oldest, with the accent used for the stacked bar + column headers.
const AGING = [
  { key: 'current', label: 'Current', tone: 'bg-emerald-500', text: 'text-emerald-600' },
  { key: 'd1_30', label: '1–30 days', tone: 'bg-amber-400', text: 'text-amber-600' },
  { key: 'd31_60', label: '31–60 days', tone: 'bg-orange-500', text: 'text-orange-600' },
  { key: 'd61_90', label: '61–90 days', tone: 'bg-rose-500', text: 'text-rose-600' },
  { key: 'd90plus', label: '90+ days', tone: 'bg-red-700', text: 'text-red-700' },
];

const FORECAST = [
  { key: 'overdue', label: 'Overdue', hint: 'Past due — collect now', danger: true },
  { key: 'next7', label: 'Next 7 days' },
  { key: 'next30', label: 'Next 30 days' },
  { key: 'next60', label: 'Next 60 days' },
  { key: 'next90', label: 'Next 90 days' },
  { key: 'later', label: 'Later' },
  { key: 'onDelivery', label: 'On delivery', hint: 'Due when the parcel arrives' },
];

const STAGE_BADGE = {
  1: { label: 'Reminder sent', cls: 'bg-amber-100 text-amber-700' },
  2: { label: 'Second notice', cls: 'bg-orange-100 text-orange-700' },
  3: { label: 'On hold', cls: 'bg-red-100 text-red-700' },
};

function Tile({ label, value, sub, tone }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone || 'text-text-primary'}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-text-muted">{sub}</div>}
    </div>
  );
}

// A single stacked bar showing the aging mix (proportional to AUD in each bucket).
function AgingBar({ totals }) {
  const sum = AGING.reduce((a, b) => a + (totals[b.key] || 0), 0);
  if (sum <= 0) return null;
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-raised">
      {AGING.map((b) =>
        totals[b.key] > 0 ? (
          <div key={b.key} className={b.tone} style={{ width: `${(totals[b.key] / sum) * 100}%` }} title={`${b.label}: ${AUD(totals[b.key])}`} />
        ) : null
      )}
    </div>
  );
}

export default function Receivables() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReceivables()
      .then((d) => {
        setData(d);
        setError('');
      })
      .catch((e) => setError(e.response?.data?.error?.message || 'Could not load receivables.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-text-muted">Loading…</div>;
  if (error) return <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">{error}</div>;

  const { summary, aging, forecast } = data;
  const forecastMax = Math.max(1, ...FORECAST.map((f) => forecast[f.key] || 0));

  return (
    <>
      <PageHeader
        title="Receivables"
        subtitle="What clients owe, how far past due, and when the cash is expected — combined in AUD. Overdue payments escalate automatically."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Outstanding" value={AUD(summary.outstandingAud)} sub={`${summary.totalInstallments} open installment${summary.totalInstallments === 1 ? '' : 's'}`} />
        <Tile label="Overdue" value={AUD(summary.overdueAud)} tone="text-rose-600" sub={`${summary.overdueInstallments} installment${summary.overdueInstallments === 1 ? '' : 's'} past due`} />
        <Tile label="Not yet due" value={AUD(summary.currentAud)} tone="text-emerald-600" sub="Current — within terms" />
        <Tile
          label="Clients in arrears"
          value={summary.overdueClients}
          tone={summary.overdueClients ? 'text-rose-600' : 'text-text-primary'}
          sub={summary.onHoldClients ? `${summary.onHoldClients} on hold (final notice)` : 'No accounts on hold'}
        />
      </div>

      {/* Cash-flow forecast */}
      <section className="mt-6">
        <h2 className="mb-2 text-sm font-semibold text-text-primary">Cash-flow forecast</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {FORECAST.map((f) => (
            <div key={f.key} className={`rounded-lg border p-3 ${f.danger && forecast[f.key] > 0 ? 'border-rose-200 bg-rose-50/50' : 'border-line bg-surface'}`}>
              <div className="text-[11px] font-medium text-text-muted">{f.label}</div>
              <div className={`mt-1 text-lg font-semibold tabular-nums ${f.danger && forecast[f.key] > 0 ? 'text-rose-600' : 'text-text-primary'}`}>{AUD(forecast[f.key])}</div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-raised">
                <div className={`h-full ${f.danger ? 'bg-rose-400' : 'bg-accent'}`} style={{ width: `${((forecast[f.key] || 0) / forecastMax) * 100}%` }} />
              </div>
              {f.hint && <div className="mt-1 text-[10px] text-text-muted">{f.hint}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* AR aging */}
      <section className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">Aged receivables</h2>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
            {AGING.map((b) => (
              <span key={b.key} className="inline-flex items-center gap-1 text-text-muted">
                <span className={`inline-block h-2 w-2 rounded-sm ${b.tone}`} /> {b.label}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-line bg-surface p-4 shadow-sm">
          <AgingBar totals={aging.totals} />
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {AGING.map((b) => (
              <div key={b.key}>
                <div className="text-[11px] text-text-muted">{b.label}</div>
                <div className={`text-sm font-semibold tabular-nums ${b.text}`}>{AUD(aging.totals[b.key])}</div>
              </div>
            ))}
          </div>
        </div>

        {aging.byClient.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
            No outstanding receivables. Every payment is settled. 🎉
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line bg-surface shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 text-right font-medium">Outstanding</th>
                  {AGING.map((b) => (
                    <th key={b.key} className="px-4 py-2 text-right font-medium">{b.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aging.byClient.map((c) => {
                  const badge = STAGE_BADGE[c.worstStage];
                  return (
                    <tr key={c.companyId || c.company} className="border-b border-line last:border-0">
                      <td className="px-4 py-3">
                        <div className="font-medium text-text-primary">{c.company}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          {c.store && <span className="text-[10px] text-text-muted">{storeShort(c.store)}</span>}
                          {c.currencies.length > 0 && <span className="text-[10px] text-text-muted">{c.currencies.join(' · ')}</span>}
                          {c.oldestDaysOverdue > 0 && <span className="text-[10px] text-rose-600">{c.oldestDaysOverdue}d overdue</span>}
                          {badge && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}>{badge.label}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-text-primary">{AUD(c.outstandingAud)}</td>
                      {AGING.map((b) => (
                        <td key={b.key} className={`px-4 py-3 text-right tabular-nums ${c.aging[b.key] > 0 ? b.text : 'text-text-muted'}`}>
                          {c.aging[b.key] > 0 ? AUD(c.aging[b.key]) : '—'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line bg-surface-raised/40 text-sm font-semibold">
                  <td className="px-4 py-3 text-text-primary">Total</td>
                  <td className="px-4 py-3 text-right tabular-nums text-text-primary">{AUD(summary.outstandingAud)}</td>
                  {AGING.map((b) => (
                    <td key={b.key} className="px-4 py-3 text-right tabular-nums text-text-secondary">{AUD(aging.totals[b.key])}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <p className="mt-4 text-xs text-text-muted">
        Combined in AUD at today's mid-market rates · generated {new Date(data.generatedAt).toLocaleString()}
      </p>
    </>
  );
}
